import { listAppRecommendations } from '@wix/ambassador-funnel-app-recommendation-v1-app-recommendation/http'
import { AppRecommendation } from '@wix/ambassador-funnel-app-recommendation-v1-app-recommendation/types'
import { Box, Button, Checkbox, Image, Text } from '@wix/design-system'
import {
  ChoiceQuestionContentComponent,
  GroupContentComponent,
  useContent,
  useExperiments,
  useMetadata,
  useNext,
} from '@wix/funnel-builder-lizard'
import { tryCatch } from '@wix/funnel-utils'
import classNames from 'classnames'
import React, { FC, ReactNode, useEffect, useState } from 'react'

import { ambassadorHttpClient } from '../../../api/httpClient'
import { usePageLogger } from '../../../bi/mfi'
import { resolveCategoryFromBusinessTerm } from '../../../category'
import { useStorageContext } from '../../../context/StorageContext/StorageContext'
import { WixMobileBackButton } from '../../reusable/WixMobileBackButton/WixMobileBackButton'
import { WixMobileContentWrapper } from '../../reusable/WixMobileContentWrapper/WixMobileContentWrapper'
import { WixMobileDescription } from '../../reusable/WixMobileDescription/WixMobileDescription'
import { WixMobileFooter } from '../../reusable/WixMobileFooter/WixMobileFooter'
import { WixMobileHeader } from '../../reusable/WixMobileHeader/WixMobileHeader'
import { WixMobileHeading } from '../../reusable/WixMobileHeading/WixMobileHeading'
import { WixMobilePage } from '../../reusable/WixMobilePage/WixMobilePage'
import { WixMobileSkipButton } from '../../reusable/WixMobileSkipButton/WixMobileSkipButton'
import { WixMobileStepProgressBar } from '../../reusable/WixMobileStepProgressBar/WixMobileStepProgressBar'
import {
  type App,
  AppLevel,
  FALLBACK_APPS,
  getBIAnswer,
  getBIAppInstallation,
  mergeAppsData,
  segmentToAppIds,
  segmentToAppIdsV2,
} from './common'
import { wixMobileAppsDataHooks as DataHooks } from './WixMobileApps.dataHooks'
import s from './WixMobileApps.scss'

export type Content = {
  builtInLabel: string
  back: string
  next: string
  skip: string
  stepProgressBar: GroupContentComponent<{ visible: boolean; total: number; current: number }>
  question: ChoiceQuestionContentComponent<{ title: string; subtitle: string }, App>
}

type ItemProps = {
  app: App
  isActive: boolean
  onClick: (appDefId: string) => void
}

const Item: FC<ItemProps> = ({ app, isActive, onClick }) => (
  <li
    className={classNames(s.WixMobileApps__item, { [s['WixMobileApps__item--active']]: isActive })}
    onClick={() => onClick(app.appDefId)}
    data-hook={DataHooks.appItem({ key: app.optionKey })}
  >
    <Image className={s.WixMobileApps__itemImage} src={app.icon} transparent />
    <div className={s.WixMobileApps__itemContent}>
      <Box className={s.WixMobileApps__itemTitle}>
        <Text weight="normal">{app.name}</Text>
      </Box>
      <Text size="small">{app.description}</Text>
    </div>
    <Checkbox checked={isActive} dataHook={DataHooks.appItemCheckbox({ key: app.optionKey })} />
  </li>
)

export const WixMobileApps: FC = () => {
  const [values, setValues] = useState<string[]>([])
  const [mergedApps, setMergedApps] = useState<App[]>([])
  const [{ biSlug: flowBiSlug }] = useMetadata()
  const { content } = useContent<Content>()
  const { category, save } = useStorageContext()
  const pageLogger = usePageLogger()
  const next = useNext()
  const experiments = useExperiments()
  const { industryId, structureId, businessSegmentId } = resolveCategoryFromBusinessTerm(
    category?.businessTerm,
    category?.businessSegmentId,
  )

  const onToggle = (value: string) => {
    pageLogger.clickAnswer({
      questionKey: content.question.questionKey,
      buttonName: content.question.options.find(o => o.optionKey === value)?.biSlug || value,
    })

    if (values.includes(value)) {
      setValues(values.filter(v => v !== value))
    } else {
      setValues([...values, value])
    }
  }

  const appsSelectedBI = () => {
    const { appId, builtInAppId } = getBIAppInstallation(mergedApps, values)

    pageLogger.appsSelected({ app_id: appId, built_in_app_id: builtInAppId })
  }

  const saveApps = () =>
    save({
      appDefIds: values
        .map(optionKey => mergedApps.find(a => a.optionKey === optionKey)?.appDefId)
        .filter(a => typeof a === 'string') as string[],
    })

  const onSkipButtonClick = () => {
    pageLogger.skip({
      payload: getBIAnswer(content.question.options, mergedApps, values),
    })

    appsSelectedBI()
    saveApps()
    next({ [content.question.questionKey]: { skipped: true } })
  }

  const onNextButtonClick = () => {
    pageLogger.answer({
      answer: getBIAnswer(content.question.options, mergedApps, values),
    })

    appsSelectedBI()
    saveApps()
    next({ [content.question.questionKey]: { values } })
  }

  const onBackButtonClick = () => {
    pageLogger.back({
      payload: getBIAnswer(content.question.options, mergedApps, values),
    })

    saveApps()
    window.history.back()
  }

  useEffect(() => {
    const doLoad = async () => {
      const request = listAppRecommendations({
        origin: flowBiSlug,
        structureId,
        industryId,
      })
      const [, response] = await tryCatch(() => ambassadorHttpClient.request(request))
      const recommendedApps = (response?.data.apps || FALLBACK_APPS) as (AppRecommendation & { level?: AppLevel })[]
      const merged = mergeAppsData(recommendedApps, content.question.options)

      const isMobileSectorToApp = experiments['specs.funnel.MobileSectorToApp'] === 'true'
      let prechecked: string[]
      let sorted: typeof merged
      if (isMobileSectorToApp) {
        const mapping = experiments['specs.funnel.SectorToAppV2'] === 'true' ? segmentToAppIdsV2 : segmentToAppIds
        const sectorAppIds = mapping[businessSegmentId] || []
        prechecked = merged.filter(app => sectorAppIds.includes(app.appDefId)).map(app => app.optionKey)
        sorted = [
          ...merged.filter(app => sectorAppIds.includes(app.appDefId)),
          ...merged.filter(app => !sectorAppIds.includes(app.appDefId)),
        ]
      } else {
        prechecked = merged
          .filter(app => app.level === 'built-in' || app.level === 'preferred')
          .map(app => app.optionKey)
        sorted = merged
      }

      pageLogger.screenView({
        initialData: getBIAnswer(content.question.options, sorted, values),
      })

      setMergedApps(sorted)
      setValues(prechecked)
    }
    void doLoad()
  }, [])

  return (
    <WixMobilePage>
      <WixMobileHeader
        leftContent={
          (content.back && (
            <WixMobileBackButton dataHook={DataHooks.back()} onBack={onBackButtonClick} label={content.back} />
          )) ||
          undefined
        }
        rightContent={
          (content.skip && (
            <WixMobileSkipButton dataHook={DataHooks.skip()} onSkip={onSkipButtonClick} label={content.skip} />
          )) ||
          undefined
        }
      />
      <WixMobileContentWrapper>
        {content.stepProgressBar.content.visible && (
          <WixMobileStepProgressBar
            current={content.stepProgressBar.content.current}
            total={content.stepProgressBar.content.total}
            dataHook={DataHooks.stepProgressBar()}
          />
        )}
        <WixMobileHeading dataHook={DataHooks.title()}>{content.question.content.title}</WixMobileHeading>
        <WixMobileDescription dataHook={DataHooks.subtitle()}>{content.question.content.subtitle}</WixMobileDescription>
        <ul className={s.WixMobileApps}>
          {mergedApps.map(app => (
            <Item
              key={app.appDefId}
              app={app}
              isActive={values.includes(app.optionKey)}
              onClick={() => onToggle(app.optionKey)}
            />
          ))}
        </ul>
      </WixMobileContentWrapper>
      {content.next && (
        <WixMobileFooter>
          <Button
            size="large"
            fullWidth
            disabled={!values.length}
            onClick={onNextButtonClick}
            dataHook={DataHooks.next()}
          >
            {content.next}
          </Button>
        </WixMobileFooter>
      )}
    </WixMobilePage>
  )
}
